/**
 * Operator-only Pipeline-tab tenant switcher (dev/ops).
 *
 * Lets a platform operator flip the *read* scope of the MVP Tests + ER Quality
 * tabs between the dev tenants (gwh-mfg / gwh-golden / xc-staging) WITHOUT
 * re-login. The selection is a tenant SLUG sent as a `tenant_override` query
 * param on those tabs' READ (GET) requests only — writes stay bound to the JWT
 * tenant. The server (`pipeline_read_tenant` shared dependency) gates the
 * override to platform operators whose home tenant is internal (s1168-1: the
 * `reliability:view_all_tenants` claim plus a live per-user grant, never a role),
 * so this is a UX affordance over a server-enforced boundary, never the boundary
 * itself. A 403 on an overridden read drops the override (see
 * `clearDeniedPipelineOverride`), so a claim the server no longer honors falls
 * back to the home tenant instead of leaving the tabs on a 403.
 *
 * Keep INTERNAL_TENANTS in sync with the backend
 * (shared/src/askwingo_shared/auth/pipeline_tenant.py).
 */

import { getAuthToken } from '@/contexts/authTokenManager';

export interface InternalTenant {
  slug: string;
  /** Deterministic tenant_id (askwingo_shared.config.dev_identity). */
  id: string;
  label: string;
}

export const INTERNAL_TENANTS: readonly InternalTenant[] = [
  { slug: 'gwh-mfg', id: '00000000-0000-0000-0000-000000000010', label: 'GWH Mfg · prod' },
  { slug: 'gwh-golden', id: '00000000-0000-0000-0000-0000000000f0', label: 'GWH Golden · fixture' },
  { slug: 'xc-staging', id: '99999999-9999-9999-9999-999999999999', label: 'XC Staging' },
] as const;

const STORAGE_KEY = 'askwingo-pipeline-tenant-override';

/**
 * The permissions a token must claim before the switcher is offered: the route's
 * own MANAGE_TENANT gate and the platform-operator grant the resolver checks
 * (askwingo_shared.auth.pipeline_tenant.resolve_pipeline_read_tenant).
 */
export const PIPELINE_OVERRIDE_PERMISSIONS = [
  'manage:tenant',
  'reliability:view_all_tenants',
] as const;

// Module-level current override (null = read the JWT/home tenant). The axios
// interceptors read this synchronously on every request, so it must be updated
// before a read fires after a switch.
let currentOverride: string | null = null;

export function getPipelineTenantOverride(): string | null {
  return currentOverride;
}

/** Set (or clear, with null) the active read override + persist it. */
export function setPipelineTenantOverride(slug: string | null): void {
  currentOverride = slug;
  try {
    if (slug) window.localStorage.setItem(STORAGE_KEY, slug);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage may be unavailable (private mode, etc.) */
  }
}

export function readStoredPipelineTenantOverride(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function isInternalTenantSlug(slug: string | null | undefined): boolean {
  return !!slug && INTERNAL_TENANTS.some((t) => t.slug === slug);
}

export function internalTenantById(id: string | null | undefined): InternalTenant | null {
  return INTERNAL_TENANTS.find((t) => t.id === id) ?? null;
}

export function internalTenantBySlug(slug: string | null | undefined): InternalTenant | null {
  return INTERNAL_TENANTS.find((t) => t.slug === slug) ?? null;
}

// ---------------------------------------------------------------------------
// A 403 on an overridden read: drop the override, tell the hook
// ---------------------------------------------------------------------------

/** Told the bearer token that SENT the refused request (null if it carried none). */
type DeniedListener = (deniedToken: string | null) => void;
const deniedListeners = new Set<DeniedListener>();

/** Called when the server refuses an override; returns the unsubscribe. */
export function subscribePipelineOverrideDenied(listener: DeniedListener): () => void {
  deniedListeners.add(listener);
  return () => {
    deniedListeners.delete(listener);
  };
}

interface DeniedShape {
  response?: { status?: number };
  config?: {
    params?: Record<string, unknown>;
    headers?: { get?: (name: string) => unknown; Authorization?: unknown; authorization?: unknown };
  };
}

/** The bearer token a request was sent with, read off its own headers. */
function senderToken(headers: NonNullable<DeniedShape['config']>['headers']): string | null {
  if (!headers) return null;
  const raw =
    (typeof headers.get === 'function' ? headers.get('Authorization') : undefined) ??
    headers.Authorization ??
    headers.authorization;
  return typeof raw === 'string' && raw.startsWith('Bearer ') ? raw.slice('Bearer '.length) : null;
}

/**
 * Axios response-error interceptor for the Pipeline read clients. When a request
 * that carried `tenant_override` comes back 403, the override (module value and
 * the stored key) is cleared and listeners are told, so the tabs refetch the home
 * tenant. A refusal of a request sent with an OLDER token than the current one
 * (a 403 landing after a refresh) says nothing about the current token, so it is
 * ignored. The error is always re-thrown: the caller still sees its request fail.
 */
export function clearDeniedPipelineOverride(error: unknown): Promise<never> {
  const e = (error ?? {}) as DeniedShape;
  const sent = e.config?.params?.tenant_override;
  if (e.response?.status === 403 && typeof sent === 'string' && sent) {
    const sender = senderToken(e.config?.headers);
    const current = getAuthToken();
    if (!sender || !current || sender === current) {
      setPipelineTenantOverride(null);
      deniedListeners.forEach((listener) => listener(sender));
    }
  }
  return Promise.reject(error);
}
